import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qaqcApi, type ConcreteSetup } from '@/lib/apiClient';
import { Button } from '@/components';

/**
 * The job's concrete SUPPLIER and BLANKET POs.
 *
 * These are the two columns of the office's phone-side "Concrete Pour Schedule"
 * sheet that the tracker never held (PE feedback, 2026-07-31: "we are missing the
 * supplier and BPO — we could also tie these to the job itself and make them show
 * on the left side of the schedule, since knowing the supplier up front is key to
 * streamlining the process"). Recorded once here; read on the pour schedule grid
 * and on every pour card for the job.
 *
 * The estimator raises the blanket PO (issue #368) and the PM checks it is here;
 * the SUPPLIER varies by job — estimator at bid, or PM at buyout. Gated on
 * projects.edit, which all three already hold. ⚠️ Neither half is the mix
 * register's PM rule; see the note in concrete-setup.service.ts for why they
 * differ and how this comment has been wrong twice. Field leaders see it
 * read-only, which is what they want — knowing the supplier is the point.
 */
export default function ConcreteSetupCard({ projectId, canEdit }: {
  projectId: number; canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const query = useQuery({
    queryKey: ['qaqc-concrete-setup', projectId],
    queryFn: () => qaqcApi.getConcreteSetup(projectId),
  });
  const setup = query.data ?? null;
  const hasAny = Boolean(setup?.supplier || setup?.concreteBpo || setup?.aggregateBpo || setup?.notes);

  return (
    <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
      <h2 className="pw-card-hd" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <span>Concrete Ordering</span>
        {canEdit && !editing && (
          <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
                  onClick={() => setEditing(true)}>
            {hasAny ? 'Edit' : 'Set supplier & BPO'}
          </button>
        )}
      </h2>

      <div style={{ fontSize: 12, color: 'var(--ew-n-500)', padding: '10px var(--ew-s-4) 0' }}>
        Who this job buys concrete from, and the blanket POs it draws against. Set once here and it shows on every pour card and
        beside the job on the pour schedule — so the office knows who it's calling before it opens the order.
      </div>

      {editing ? (
        <SetupForm
          projectId={projectId}
          existing={setup}
          onDone={() => setEditing(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['qaqc-concrete-setup', projectId] });
            // Supplier/BPO ride along on the pour cards, so the schedule grid
            // and every open card need a refetch to pick the change up.
            qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
          }}
        />
      ) : query.isLoading ? (
        <div style={{ padding: 'var(--ew-s-4)', fontSize: 13, color: 'var(--ew-n-500)' }}>Loading…</div>
      ) : !hasAny ? (
        <div style={{ padding: 'var(--ew-s-4)', fontSize: 13, color: 'var(--ew-n-500)' }}>
          Nothing recorded yet.{canEdit ? ' The pour schedule shows “No supplier / BPO set” against this job until it is.' : ''}
        </div>
      ) : (
        <div style={{ padding: '12px var(--ew-s-4) var(--ew-s-4)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
            <Read label="Supplier" value={setup!.supplier} />
            <Read label="Concrete BPO" value={setup!.concreteBpo} />
            <Read label="Aggregate BPO" value={setup!.aggregateBpo} />
          </div>
          {setup!.notes && (
            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ew-dark)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
              {setup!.notes}
            </div>
          )}
          {setup!.updatedBy && (
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ew-n-400)' }}>
              Last set by {setup!.updatedBy}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Read({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div style={{ font: 'var(--ew-fw-semibold) 10.5px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', marginBottom: 5 }}>{label}</div>
      <div style={{ font: 'var(--ew-fw-semibold) 14px/1.3 var(--ew-font)', color: value ? 'var(--ew-dark)' : 'var(--ew-n-400)' }}>{value || '—'}</div>
    </div>
  );
}

function SetupForm({ projectId, existing, onDone, onSaved }: {
  projectId: number;
  existing: ConcreteSetup | null;
  onDone: () => void;
  onSaved: () => void;
}) {
  const [supplier, setSupplier] = useState(existing?.supplier ?? '');
  const [concreteBpo, setConcreteBpo] = useState(existing?.concreteBpo ?? '');
  const [aggregateBpo, setAggregateBpo] = useState(existing?.aggregateBpo ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => qaqcApi.saveConcreteSetup(projectId, {
      supplier: supplier.trim() || null,
      concreteBpo: concreteBpo.trim() || null,
      aggregateBpo: aggregateBpo.trim() || null,
      notes: notes.trim() || null,
    }),
    onSuccess: () => { onSaved(); onDone(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Failed to save'),
  });

  const field: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 9px',
    font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff', color: 'var(--ew-dark)', width: '100%',
  };
  const label: React.CSSProperties = { font: 'var(--ew-fw-semibold) 10.5px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', display: 'block', marginBottom: 4 };

  return (
    <div style={{ padding: 'var(--ew-s-4)', background: 'var(--ew-n-50, #FAFAFA)', borderTop: '1px solid var(--ew-n-100)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Supplier</label>
          <input style={field} value={supplier} onChange={(e) => setSupplier(e.target.value)}
                 placeholder="Advance Etown" autoFocus />
        </div>
        <div>
          <label style={label}>Concrete BPO</label>
          <input style={field} value={concreteBpo} onChange={(e) => setConcreteBpo(e.target.value)} placeholder="BPO-26453" />
        </div>
        <div>
          <label style={label}>Aggregate BPO</label>
          <input style={field} value={aggregateBpo} onChange={(e) => setAggregateBpo(e.target.value)} placeholder="BPO-26190" />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={label}>Notes</label>
        <input style={field} value={notes} onChange={(e) => setNotes(e.target.value)}
               placeholder="Anything the office should know when ordering on this job" />
      </div>
      <div style={{ font: '11px/1.4 var(--ew-font)', color: 'var(--ew-n-400)', marginTop: 8 }}>
        Blanket POs are typed as they read on the PO — the tracker doesn't link them to Sage. Clearing a field removes it.
      </div>

      {error && (
        <div style={{ margin: '10px 0 0', padding: '7px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onDone} disabled={save.isPending}>Cancel</Button>
        <Button variant="primary" size="sm" isLoading={save.isPending}
                onClick={() => { setError(null); save.mutate(); }}>
          Save
        </Button>
      </div>
    </div>
  );
}
